// External modules
import React from 'react';

// Internal modules
import { Link } from '../../Common';
import { TERMS_MAP } from '../constants';
import { buildMPPUrl, buildTermsUrl, getContent, getLocale } from '@/lib';
import { getFeeInformationServiceDescriptionKey, logLinkClick } from '../utils';
import type { GenericObjectType } from '@/types';

interface LinkProps {
  embeddedLinkText: string;
  flowType: string;
  subPageAnchorNumber?: string;
}

export const getPrivacyPolicyEmbeddedLink = ({
  embeddedLinkText,
  flowType,
  subPageAnchorNumber,
}: LinkProps): JSX.Element => {
  const {
    privacyPolicyLink: { path },
  } = TERMS_MAP;

  let href = buildTermsUrl({ path });
  if (subPageAnchorNumber) {
    href += `#${subPageAnchorNumber}`;
  }

  return (
    <Link
      href={href}
      onClick={() => logLinkClick(`${flowType}-privacy-policy`, path)}
      testId={`${flowType}-privacy-policy`}
      text={getContent(embeddedLinkText)}
    />
  );
};

export const getTermsAndConditionsEmbeddedLink = ({
  embeddedLinkText,
  flowType,
}: LinkProps): JSX.Element => {
  const {
    termsAndConditionsLink: { path },
  } = TERMS_MAP;
  return (
    <Link
      href={buildTermsUrl({ path })}
      onClick={() => logLinkClick(`${flowType}-terms-and-conditions`, path)}
      text={getContent(embeddedLinkText)}
    />
  );
};

export const getUserAgreementLink = ({
  embeddedLinkText,
  flowType,
}: LinkProps): JSX.Element => {
  const {
    userAgreementLink: { getDynamicPath },
  } = TERMS_MAP;

  return (
    <Link
      href={buildTermsUrl({ getDynamicPath })}
      onClick={() =>
        logLinkClick(`${flowType}-user-agreement`, getDynamicPath())
      }
      testId={`${flowType}-user-agreement`}
      text={getContent(embeddedLinkText)}
    />
  );
};

export const streamlineGuestTermsEmbeddedLinks = (): GenericObjectType => {
  const { termsLink } = TERMS_MAP;
  return {
    countryUserAgreementLink: getUserAgreementLink({
      embeddedLinkText: 'compliance.countryUserAgreement',
      flowType: 'guest',
    }),
    moreInformationLink: (
      <Link
        href={buildMPPUrl({ mppPath: 'paypal-and-your-data' })}
        text={getContent('compliance.moreInformation')}
      />
    ),
    privacyPolicyLink: getPrivacyPolicyEmbeddedLink({
      embeddedLinkText: 'compliance.privacyPolicy',
      flowType: 'guest',
    }),
    privacyPolicyStatementLink: getPrivacyPolicyEmbeddedLink({
      embeddedLinkText: 'compliance.privacyPolicyStatement',
      flowType: 'guest',
    }),
    privacyPolicyV2Link: getPrivacyPolicyEmbeddedLink({
      embeddedLinkText: 'compliance.privacyPolicyV2',
      flowType: 'guest',
    }),
    termsLink: (
      <Link
        href={buildTermsUrl({ path: termsLink.path })}
        onClick={() => logLinkClick('guest-terms', termsLink.path)}
        testId="guest-terms"
        text={getContent('compliance.terms')}
      />
    ),
    termsV2Link: getTermsAndConditionsEmbeddedLink({
      embeddedLinkText: 'compliance.termsUpperCase',
      flowType: 'guest',
    }),
    userAgreementLink: getUserAgreementLink({
      embeddedLinkText: 'compliance.userAgreement',
      flowType: 'guest',
    }),
  };
};

export const streamlineSignupTermsEmbeddedLinks = (): GenericObjectType => {
  const isoCode = getLocale()?.country;
  const {
    serviceDescriptionLink,
    cfsgpdsLink,
    eSignConsent,
    eSignDisclosureAndConsent,
    acceptableUsePolicyLink,
    sanctionsLink,
  } = TERMS_MAP;
  return {
    acceptableUsePolicyLink: (
      <Link
        href={buildTermsUrl({ path: acceptableUsePolicyLink.path })}
        onClick={() =>
          logLinkClick('signup-acceptable-use', acceptableUsePolicyLink.path)
        }
        testId="signup-acceptable-use"
        text={getContent('compliance.acceptableUsePolicy')}
      />
    ),
    // Commission Information is the same as Fee Information for some countries
    commissionInformationLink: (
      <Link
        href={buildTermsUrl({ useFunction: 'feeInformation' })}
        onClick={() => logLinkClick('commission-information', 'ua/pdf')}
        testId="commission-information"
        text={getContent('compliance.commissionInformation')}
      />
    ),
    eSignConsentLink: (
      <Link
        href={buildTermsUrl({ path: eSignConsent.path })}
        onClick={() => logLinkClick('signup-esign-consent', eSignConsent.path)}
        testId="signup-esign-consent"
        text={getContent('compliance.eSignConsent')}
      />
    ),
    eSignDisclosureAndConsentLink: (
      <Link
        href={buildTermsUrl({ path: eSignDisclosureAndConsent.path })}
        onClick={() =>
          logLinkClick(
            'signup-esign-disclosure-use',
            eSignDisclosureAndConsent.path,
          )
        }
        testId="esign-disclosure-consent"
        text={getContent('compliance.eSignDisclosureAndConsent')}
      />
    ),
    feeInformationLink: (
      <Link
        href={buildTermsUrl({ useFunction: 'feeInformation' })}
        onClick={() => logLinkClick('fee-information', 'ua/pdf')}
        testId="fee-information"
        text={getContent(getFeeInformationServiceDescriptionKey({ isoCode }))}
      />
    ),
    financialServiceAndDisclosureLink: (
      <Link
        href={buildTermsUrl({ path: cfsgpdsLink.path })}
        onClick={() => logLinkClick('signup-cfsgpds', cfsgpdsLink.path)}
        testId="signup-cfsgpds"
        text={getContent('compliance.cfsgpds')}
      />
    ),
    paypalUserAgreementLink: getUserAgreementLink({
      embeddedLinkText: 'compliance.paypalUserAgreement',
      flowType: 'signup',
    }),
    privacyPolicyLink: getPrivacyPolicyEmbeddedLink({
      embeddedLinkText: 'compliance.privacyPolicy',
      flowType: 'signup',
    }),
    privacyPolicyStatementLink: getPrivacyPolicyEmbeddedLink({
      embeddedLinkText: 'compliance.privacyPolicyStatement',
      flowType: 'signup',
    }),
    privacyPolicyV2Link: getPrivacyPolicyEmbeddedLink({
      embeddedLinkText: 'compliance.privacyPolicyV2',
      flowType: 'signup',
    }),
    rightOfWithdrawalLink: (
      <Link
        href={buildTermsUrl({ useFunction: 'rightOfWithdrawal' })}
        onClick={() =>
          logLinkClick('right-of-withdrawal', 'ua/useragreement-full')
        }
        testId="right-of-withdrawal"
        text={getContent('compliance.rightOfWithdrawal')}
      />
    ),
    sanctionsLink: (
      <Link
        href={buildTermsUrl({ path: sanctionsLink.path })}
        onClick={() =>
          logLinkClick('signup-compliance-sanctions', sanctionsLink.path)
        }
        testId="signup-compliance-sanctions"
        text={getContent('compliance.sanctions')}
      />
    ),
    serviceDescriptionLink: (
      <Link
        href={buildTermsUrl({ path: serviceDescriptionLink.path })}
        onClick={() =>
          logLinkClick('signup-cfsgpds', serviceDescriptionLink.path)
        }
        testId="signup-service-description"
        text={getContent(getFeeInformationServiceDescriptionKey({ isoCode }))}
      />
    ),
    termsAndConditionsLink: getTermsAndConditionsEmbeddedLink({
      embeddedLinkText: 'compliance.termsAndConditionsLowerCase',
      flowType: 'signup',
    }),
    termsAndConditionsLinkUpperCase: getTermsAndConditionsEmbeddedLink({
      embeddedLinkText: 'compliance.termsAndConditionsUpperCase',
      flowType: 'signup',
    }),
    userAgreementLink: getUserAgreementLink({
      embeddedLinkText: 'compliance.userAgreement',
      flowType: 'signup',
    }),
  };
};
